/**************************************************************************************************/
/** @import Node Packages                                                                        **/
/**************************************************************************************************/
import { defineComponent } from 'vue'

/**************************************************************************************************/
/** @import Components/Global                                                                    **/
/**************************************************************************************************/
import * as BasicHeader from '@/Components/Global/BasicHeader/template.vue'
import * as DataTable from '@/Components/Global/DataTable/template.vue'

/**************************************************************************************************/
/** @import Components/Modules                                                                   **/
/**************************************************************************************************/
import * as AttributesList from '@/Components/Modules/CheckList/AttributesList/template.vue'

/**************************************************************************************************/
/** @import Constants                                                                            **/
/**************************************************************************************************/
import { Server } from '@/Constants/Server'
import { VueRouter } from '@/Constants/VueRouter'

/**************************************************************************************************/
/** @import Helpers/Documents                                                                    **/
/**************************************************************************************************/
import { TableColumnPDF } from '@/Helpers/Documents/LegacyPDF/TablePDF'

/**************************************************************************************************/
/** @import Helpers/Utils                                                                        **/
/**************************************************************************************************/
import { TypesHelper } from '@/Helpers/Utils/TypesHelper'

/**************************************************************************************************/
/** @import Mixins                                                                                **/
/**************************************************************************************************/
import MixinBase from '@/Mixins/MixinBase'
import MixinComponent from '@/Mixins/MixinComponent'
import MixinFetch, { FetchDataResult } from '@/Mixins/MixinFetch'

/**************************************************************************************************/
/** @import Models                                                                               **/
/**************************************************************************************************/
import { TokenUserDTO } from '@/Models/TokenUserDTO'

/**************************************************************************************************/
/** @import Store/Global                                                                         **/
/**************************************************************************************************/
import Store from '@/Store/Global/Default'

/**************************************************************************************************/
/** @import Views/Modules                                                                        **/
/**************************************************************************************************/
import * as CheckListParsers from '@/Views/Modules/CheckList/MainView/response.parser'
import { GeneratePDF } from '@/Views/Modules/CheckList/MainView/pdf.document'

/**************************************************************************************************/
/** @internal Const @name CheckListManageView                                                    **/
/**************************************************************************************************/
const CheckListManageView = defineComponent(
{
	/*************************************************/
	/** @Properties                                 **/
	/*************************************************/
	name: VueRouter.Views.Modules.CheckList.ManageView.NAME, mixins: [MixinBase, MixinComponent, MixinFetch],

	components:
	{
		AttributesList: AttributesList.default,
		BasicHeader: BasicHeader.default,
		DataTable: DataTable.default
	},

	/*************************************************/
	/** @Data                                       **/
	/*************************************************/
	data()
	{
		return {
			states:
			{
				dataTableParser: CheckListParsers.parseEquipmentsResponse,
				showAttributesList: false,
				showDataTable: true
			}
		}
	},

	/*************************************************/
	/** @Hooks                                      **/
	/*************************************************/
	mounted() : void
	{
		this.InitPermissions()
		this.InitDataTable()
	},

	/*************************************************/
	/** @Computed                                   **/
	/*************************************************/
	computed:
	{
		AttributesList: function(): AttributesList.AttributesListRef
		{
			return this.$refs.attributesList as AttributesList.AttributesListRef
		},

		BasicHeader() : BasicHeader.BasicHeaderRef
		{
			return this.$refs.basicHeader as BasicHeader.BasicHeaderRef
		},

		DataTable() : DataTable.DataTableRef
		{
			return this.$refs.dataTable as DataTable.DataTableRef
		}
	},

	/*************************************************/
	/** @Methods                                    **/
	/*************************************************/
	methods:
	{
		async FetchCheckLists(page: number) : Promise<FetchDataResult | undefined>
		{
			const user = Store.getters.getStoredUser as TokenUserDTO

			// Realizar la petición al Servidor.
			const params   = { _idUser: user._idUser, itemsPerPage: this.DataTable.itemsPerPage, page }
			const response = await this.Fetch(Server.Fetching.Method.GET, Server.Routes.CheckLists.GetCheckListsFailed, params)

			// Si se obtiene una respuesta satisfactoria, continuar con el proceso.
			if (response?.status === Server.Response.StatusCodes.SUCCESS)
			{
				return response.data.body[0]
			}
		},

		async InitDataTable(page = 1) : Promise<void>
		{
			// Si se obtiene una respuesta válida, continuar con el proceso.
			const response = await this.FetchCheckLists(page)
			if (response)
			{
				const { fields, items, actions } = this.states.dataTableParser(response.data, true)
				this.DataTable.Set<DataTable.DataTableRef['states']>({ exportDataAsAsync: true, isLocalSearch: false })
				this.DataTable.UpdateElementsAndPagination(response.totalPages, fields, items, actions)
			}
		},

		InitPermissions() : void
		{
			this.DataTable.SetPermission('ACTION_PDF', true)
			this.DataTable.SetPermission('ACTION_STATUS', true)
			this.DataTable.SetPermission('ACTION_VIEW', true)
		},

		OnALCloseClick() : void
		{
			this.Set<CheckListManageViewRef['states']>({ showAttributesList: false })
		},

		OnDTButtonClick(key: string, { item }: any) : void
		{
			if (key === 'view')
			{
				const { stacked } = this.DataTable.states
				if (!(item.checkList)) return this.ShowToast('Sin CheckList', 'El equipo no tiene ningun CheckList asociado!', 'danger')

				// Dar formato a la Fecha.
				const { checkList } = item
				const _date = TypesHelper.toDatePartsFromString(checkList.date)
				const date = _date ? `${ _date.date } ${ _date.time }` : ''

				this.AttributesList.Set<AttributesList.AttributesListRef['states']>({
					type: 'actives', options: checkList.attributes,
					data: {
						date,
						active: stacked ? item.code : `${ item.code } - ${ item.groupArticle } (${ item.typeEquipment })`,
						hourmeter: checkList.hourmeter,
						comment: checkList.comment || 'Sin comentario...'
					}
				})
				this.Set<CheckListManageViewRef['states']>({ showAttributesList: true })
			}
			else if (key === 'pdf')
			{
				if (!(item.checkList)) return this.ShowToast('Sin CheckList', 'El equipo no tiene ningun CheckList asociado!', 'danger')

				// Definición de las Columnas.
				const cols: Array<TableColumnPDF> = [
					{ key: 'num', label: 'Num' },
					{ key: 'attribute', label: 'Preguntas' },
					{ key: 'status', label: 'Estado' }
				]

				// La propiedad 'attributes' cambia según la pestaña activa.
				const attributes = item.checkList.attributes

				// Definición de las Filas.
				let rowCounter = 1
				const rows: Array<any> = attributes.map((x: any) => ({ num: rowCounter++, attribute: x.name, status: x.status }))

				// Finalmente, generar el documento PDF.
				const _item = { ...item, company: item.companyName, storage: item.storageName }
				GeneratePDF(cols, rows, _item)
			}
		},

		OnDTPaginationChanged(page: number) : void
		{
			this.InitDataTable(page)
		},

		OnDTRefreshButtonClick() : void
		{
			this.InitDataTable()
		}
	}
})

/**************************************************************************************************/
/** @export Component @name CheckListManageView                                                  **/
/**************************************************************************************************/
export default CheckListManageView

/**************************************************************************************************/
/** @export Type @name CheckListManageViewRef                                                    **/
/**************************************************************************************************/
export type CheckListManageViewRef = InstanceType<typeof CheckListManageView>